import React, { useEffect, useState, useRef, useMemo, useContext } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  Grid,
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
// Note: Autocomplete+TextField retained for Stylist selector only
import { useAuth } from "../../contexts/auth";
import { useThemeMode } from "../../contexts/theme-mode";
import { Scheduler } from "@aldabil/react-scheduler";
import BarSpent from "../../components/charts/bar5";
import Donut from "../../components/charts/donuts";
import { renderCell } from "../Scheduler/CellItem";
import "./stylist.style.scss";
import { getFields } from "../Scheduler/Fields";
import { renderDetailedItem } from "../Scheduler/DetailedItem";
import { resourceFields, floatingToDate } from "../Scheduler/config";
import { SCHEDULER_TIMEZONE } from "../Scheduler/index";
import Stick from "../../components/charts/stick";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import stringToColor from "string-to-color";
import { baseAxios, Context } from "../../utils/config";
import { useScheduler } from "../Scheduler/useScheduler";
import {
  ROLE_SALON_ADMIN,
  ROLE_SYSTEM_ADMIN,
  ROLE_JDTECH_ADMIN,
} from "../../utils/constants";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Salon calendar "today" label — matches Scheduler (America/New_York). */
const getTodaySalon = () => {
  const d = dayjs().tz(SCHEDULER_TIMEZONE);
  const dayOfWeek = d.format("ddd").toUpperCase();
  const month = d.format("MMMM").toUpperCase();
  return `TODAY ${dayOfWeek} ${month} ${d.date()}, ${d.year()}`;
};

/** Noon on selected YMD in salon TZ — same safe wall-clock pattern as WeekNavigation / Scheduler header. */
const dateAtNoonInSalonTz = (ymd, tz) =>
  dayjs.tz(`${ymd} 12:00:00`, tz).toDate();

export default function StylistDashboard() {
  const calendarRef = useRef();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useThemeMode();
  const { location: globalLocation } = useContext(Context);
  const { events, resources, setEvents, setResources } = useScheduler();

  // Role booleans
  const isSystemAdmin = user.userRoles.includes(ROLE_SYSTEM_ADMIN);
  const isJDTechAdmin = user.userRoles.includes(ROLE_JDTECH_ADMIN);
  const isSalonAdmin = user.userRoles.includes(ROLE_SALON_ADMIN);
  const canSelectStylist = isSystemAdmin || isJDTechAdmin || isSalonAdmin;

  // JD-293: Use the globally selected location from the header selector.
  // Falls back to user's primary when "All Locations" (-1) is selected.
  const currentLocation = useMemo(() => {
    if (globalLocation?.location_id && globalLocation.location_id !== -1) {
      return {
        location_id: globalLocation.location_id,
        location_name: globalLocation.location_name,
        organization_id: parseInt(user.org),
      };
    }
    return {
      location_id: user.location_id,
      location_name: user.location_name,
      organization_id: parseInt(user.org),
    };
  }, [globalLocation, user.location_id, user.location_name, user.org]);

  // Selector state
  const [stylists, setStylists] = useState([]);
  const [selectedStylist, setSelectedStylist] = useState(null);

  // Dashboard data state
  const [loading, setLoading] = useState(false);
  // `BarSpent` expects [totalSpent, average $$, #transactions]
  const [totalSpent, setTotalSpent] = useState([0, 0, 0]);
  const [averageSpent, setAverageSpent] = useState([0, 0, 0]);
  const [valueCSRatio, setValueCSRatio] = useState(0);
  const [logs, setLogs] = useState([]);

  const colors = ["#8a8a8a", "#cbaa6f", "#8b4e3f"];

  const selectedDateSalonToday = dateAtNoonInSalonTz(
    dayjs().tz(SCHEDULER_TIMEZONE).format("YYYY-MM-DD"),
    SCHEDULER_TIMEZONE,
  );

  // useEffect: Load stylists when location changes
  useEffect(() => {
    if (!currentLocation) {
      setStylists([]);
      setSelectedStylist(null);
      return;
    }

    // Pure Stylist (not admin): skip fetch, set self directly
    if (!canSelectStylist) {
      setSelectedStylist({
        user_id: user.userID,
        user_first_name: user.userName.split(" ")[0] || "",
        user_last_name: user.userName.split(" ").slice(1).join(" ") || "",
        location_id: currentLocation.location_id,
        location_name: currentLocation.location_name,
      });
      setStylists([]);
      return;
    }

    // Admin roles: fetch stylists at the selected location
    const controller = new AbortController();

    const fetchStylists = async () => {
      try {
        const res = await baseAxios.get("/stylists", {
          params: { location_id: currentLocation.location_id },
          signal: controller.signal,
        });
        const data = res.data;
        setStylists(data);
        if (data.length > 0) {
          setSelectedStylist(data[0]);
        } else {
          setSelectedStylist(null);
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        toast.error("Failed to load stylists.");
        console.error("Error fetching stylists:", error);
      }
    };

    fetchStylists();

    return () => controller.abort();
  }, [currentLocation]);

  // useEffect 4: Fetch dashboard data when stylist changes
  useEffect(() => {
    if (!selectedStylist || !currentLocation) {
      setTotalSpent([0, 0, 0]);
      setAverageSpent([0, 0, 0]);
      setValueCSRatio(0);
      setLogs([]);
      setEvents([]);
      setResources([]);
      return;
    }

    const controller = new AbortController();

    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const result = await baseAxios.get("/stylist-dashboard", {
          params: {
            userId: selectedStylist.user_id,
            locationId: currentLocation.location_id,
          },
          signal: controller.signal,
        });

        // Response is wrapped: { data: { schedules, totalSpent, ... } }
        const {
          schedules,
          totalSpent,
          averageSpent,
          cs_ratio,
          client_history,
        } = result.data.data;

        const safeTriple = (v) => {
          if (Array.isArray(v) && v.length >= 3) return v.slice(0, 3);
          if (Array.isArray(v) && v.length === 2)
            return [v[0] ?? 0, v[1] ?? 0, 0];
          if (Array.isArray(v) && v.length === 1) return [v[0] ?? 0, 0, 0];
          if (typeof v === "number") return [v, 0, 0];
          return [0, 0, 0];
        };

        setTotalSpent(safeTriple(totalSpent));
        setAverageSpent(safeTriple(averageSpent));
        setValueCSRatio(cs_ratio || 0);
        const normalizedHistory = (
          Array.isArray(client_history) ? client_history : []
        )
          .map((entry) => {
            if (Array.isArray(entry) && entry.length > 0) {
              return entry[0];
            }
            return entry;
          })
          .filter(
            (entry) =>
              entry &&
              typeof entry === "object" &&
              !Array.isArray(entry) &&
              entry.client,
          );

        setLogs(normalizedHistory);

        if (Array.isArray(schedules) && schedules.length > 0) {
          const resource = {
            user_id: schedules[0].user_id,
            user_name: schedules[0].user_name,
            location_id: schedules[0].location_id,
            location_name: schedules[0].location_name,
            color: stringToColor(schedules[0].user_name),
          };

          const mappedEvents = schedules.map((schedule) => {
            return {
              start: floatingToDate(schedule.start, SCHEDULER_TIMEZONE),
              end: floatingToDate(schedule.end, SCHEDULER_TIMEZONE),
              event_id: schedule.event_id,
              title: schedule.client_name,
              user_id: schedule.user_id,
              user_name: schedule.user_name,
              client: {
                client_id: schedule.client_id,
                client_name: schedule.client_name,
              },
              location: {
                location_id: schedule.location_id,
                location_name: schedule.location_name,
              },
              uniqueServices: [
                {
                  service_id: schedule.service_id,
                  title: schedule.service_name,
                  service_shortcut: schedule.service_shortcut,
                  service_color: schedule.service_color,
                },
              ],
              services: [
                {
                  event_id: schedule.event_id,
                  service_id: schedule.service_id,
                  title: schedule.service_name,
                  service_shortcut: schedule.service_shortcut,
                },
              ],
            };
          });

          setResources([resource]);
          calendarRef.current?.scheduler?.handleState([resource], "resources");
          setEvents(mappedEvents);
        } else {
          setResources([]);
          setEvents([]);
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Error fetching dashboard data:", error);
        toast.error(error?.message || "Error fetching dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();

    return () => controller.abort();
  }, [selectedStylist, currentLocation]);

  return (
    <div className="stylist-dashboard-wrapper">
      <h2 className={"content-block"}>Lens</h2>

      {/* Selector Row - only visible to admins */}
      {canSelectStylist && (
        <div className={"responsive-paddings"} style={{ paddingBottom: 0 }}>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            {/* JD-293: Location selector removed — use global header selector */}
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                disabled={loading || stylists.length === 0}
                disableClearable
                value={selectedStylist}
                options={stylists}
                getOptionLabel={(option) =>
                  `${option.user_last_name || ""}, ${
                    option.user_first_name || ""
                  }`.replace(/^, |, $/g, "")
                }
                isOptionEqualToValue={(o, v) => o.user_id === v.user_id}
                onChange={(e, val) => setSelectedStylist(val)}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} label="Stylist" />
                )}
              />
            </Grid>
          </Grid>
        </div>
      )}

      <div className={"responsive-paddings"}>
        <div className="user-info">
          <h5>
            STYLIST:{" "}
            <span>
              {selectedStylist
                ? `${selectedStylist.user_first_name || ""} ${
                    selectedStylist.user_last_name || ""
                  }`
                    .trim()
                    .toUpperCase()
                : user.userName.toUpperCase()}
            </span>
          </h5>
          <h5>TIME PERIOD: {getTodaySalon()}</h5>
        </div>

        <div className="dashboard-body" style={{ position: "relative" }}>
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: isDark
                  ? "rgba(0, 0, 0, 0.45)"
                  : "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <div className="dashboard-grid">
            <section className="schedule-section">
              <div className="chart-title">
                {selectedStylist?.user_id === user.userID
                  ? "YOUR SCHEDULE TODAY"
                  : "SCHEDULE TODAY"}
              </div>
              <div
                className="scheduler-sider"
                onClick={() => navigate("/scheduler")}
              >
                <Scheduler
                  ref={calendarRef}
                  timeZone={SCHEDULER_TIMEZONE}
                  events={events}
                  resourceFields={resourceFields}
                  viewerExtraComponent={renderDetailedItem}
                  fields={getFields({
                    stylists: resources,
                  })}
                  week={null}
                  month={null}
                  deletable={false}
                  editable={false}
                  draggable={false}
                  disabled={true}
                  selectedDate={selectedDateSalonToday}
                  key={selectedDateSalonToday.getTime()}
                  day={{
                    startHour: 9,
                    endHour: 20,
                    step: 60,
                  }}
                  view="day"
                  eventRenderer={(item) => renderCell(item, null, false)}
                  onConfirm={null}
                  onDelete={null}
                  onEventClick={null}
                  onEventDrop={null}
                />
              </div>
            </section>

            <section className="metrics-section">
              <div className="metrics-charts-block">
                <div className="metrics-charts-titles">
                  <div className="chart-title">
                    TOTAL SPENT VS AVERAGE $$ VS TRANSACTIONS
                  </div>
                  <div className="chart-title">CS RATIO</div>
                </div>
                <div className="chart-panel metrics-charts-combined">
                  <div className="metric-block metric-block--spent">
                    <BarSpent
                      onClick={() => navigate("/stylist-spend-sql")}
                      totalSpent={totalSpent}
                      averageSpent={averageSpent}
                    />
                  </div>
                  <div className="metric-block metric-block--cs">
                    <Donut
                      color="#cbaa6f"
                      value={valueCSRatio / 1}
                      total={100}
                      label="Color to"
                      title="Services Ratio"
                      noValue={true}
                      onClick={() => navigate("/stylist-spend-sql")}
                    />
                  </div>
                </div>
              </div>

              {logs.length > 0 && (
                <div className="summary-container">
                  {logs.map((log, index) => (
                    <div
                      className="stick-card"
                      key={index}
                      onClick={() =>
                        navigate("/rolled-reports-sql", {
                          state: {
                            clientName: log.client,
                            stylistId: selectedStylist?.user_id,
                            stylistName: selectedStylist
                              ? `${selectedStylist.user_first_name || ""} ${
                                  selectedStylist.user_last_name || ""
                                }`.trim()
                              : user.userName,
                            locationId: currentLocation.location_id,
                            locationName: currentLocation.location_name,
                          },
                        })
                      }
                    >
                      <Stick
                        color={colors[index % colors.length]}
                        data={log}
                      />
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
